import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import { api } from "@/lib/api";
import { staffSessionActions } from "./staffSessionSlice";

export interface PermissionsState {
  permissions: string[];
  loading: boolean;
  error: string | null;
  requestId?: string | null;
}

const initialState: PermissionsState = {
  permissions: [],
  loading: false,
  error: null,
};

/**
 * Fetches the user's flattened permission codes from the backend.
 *
 * Expected API shape:
 *   GET /api/users/me/permissions
 *   Response: ["benefits:create", "benefits:view", "reports:run"]
 */
export const fetchUserPermissions = createAsyncThunk<string[]>(
  "permissions/fetchUserPermissions",
  async (_, { rejectWithValue }) => {
    try {
      return await api.get<string[]>("/api/users/me/permissions");
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch permissions"
      );
    }
  }
);

const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    clearPermissions() { return initialState; },
    setPermissions(state, action: PayloadAction<string[]>) {
      state.permissions = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(staffSessionActions.scopeRevoked, () => initialState)
      .addCase(staffSessionActions.contextAccepted, (_state, action) => ({
        permissions: action.payload.permissions, loading: false, error: null, requestId: null,
      }))
      .addCase(fetchUserPermissions.pending, (state, action) => {
        state.requestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.requestId = null;
        state.permissions = action.payload;
        state.loading = false;
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.requestId = null;
        state.loading = false;
        state.error = action.payload as string;
        state.permissions = [];
      });
  },
});

export const { clearPermissions, setPermissions } = permissionsSlice.actions;
export default permissionsSlice.reducer;

/** Check if the user has a specific permission. */
export function hasPermission(permissions: string[], code: string): boolean {
  return permissions.includes(code);
}

/** Check if the user has any of the specified permissions. */
export function hasAnyPermission(permissions: string[], codes: string[]): boolean {
  return codes.some((code) => permissions.includes(code));
}
